import React from 'react';
import { LinearProgress, makeStyles, Typography } from '~/lib/bn-material-ui';
import BinLevelFill from './bin-level/bin-level-fill';
import classnames from 'classnames';
import useBinLevelStyles from './bin-level/bin-level-styles';
import { useBarnLatestFeedLevel } from './bin-level/use-barn-feed-levels';
import BinLevelUnknown from '~/bins/bin-level/bin-level-unknown';
import * as d3 from 'd3';
import { translateNumber, useTranslation } from '~/translations';

const useStyles = makeStyles((theme) => {
  return {
    container: {
      position: 'relative',
      borderRight: `1px solid ${theme.palette.gray[50]}`,
    },
    binLevelFillContainer: {
      minWidth: '100%',
    },
    sideTick: {
      position: 'absolute',
      left: '100%',
      marginLeft: '0.25rem',
      marginTop: '-0.5rem',
      height: '1rem',
      lineHeight: '1rem',
      width: '5rem',
      color: theme.palette.gray[50],
      fontWeight: 'bold',
      fontSize: '.75rem',
    },
    tickLine: {
      position: 'absolute',
      left: '0',
      width: '100%',
      borderBottom: '2px dashed rgba(0, 0, 0, 0.1)',
    },
    currentLevel: {
      position: 'absolute',
      left: '0',
      top: '50%',
      width: '100%',
      marginTop: '-0.5rem',
      color: theme.palette.gray[80],
      textAlign: 'center',
      '& > *': {
        fontWeight: 'bold',
        fontSize: 'small',
      },
    },
    additionalVolume: {
      backgroundColor: theme.palette.blue[30],
    },
  };
});

export function BarnFeedLevelDisplay({ currentLevel, currentEstimatedLevel, volume, estimatedVolume, additionalLevel, additionalVolume, total, totalVolume, unit, precision = 2, heightPx, widthPx }) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const binLevelStyles = useBinLevelStyles();

  if (!Number.isFinite(currentLevel)) {
    return (
      <div style={{ height: `${heightPx}px`, width: `${widthPx}px` }}>
        <BinLevelUnknown />
      </div>
    );
  }

  const assumedCurrentLevel = currentEstimatedLevel ?? currentLevel;
  const assumedCurrentVolume = estimatedVolume ?? volume;
  const sideTicks = getSideTicks(total);
  const binFillLevel = (assumedCurrentLevel + (Number.isFinite(additionalLevel) ? additionalLevel : 0)).toFixed(precision);
  return (
    <div style={{ height: `${heightPx}px`, width: `${widthPx}px` }} className={classnames([binLevelStyles.box, styles.container])}>
      <div className={classnames(binLevelStyles.solid, styles.binLevelFillContainer)}>
        {Number.isFinite(additionalVolume) && (
          <BinLevelFill
            volume={Math.min(additionalVolume + assumedCurrentVolume, totalVolume)}
            totalVolume={totalVolume}
            className={styles.additionalVolume}
          />
        )}
        <BinLevelFill volume={assumedCurrentVolume} totalVolume={totalVolume}/>
      </div>
      <div className={styles.currentLevel}><Typography>{t('numberWithUntranslatedUnit', { number: translateNumber(binFillLevel, i18n.language), unit })}</Typography></div>
      {sideTicks.map((tick) => {
        const top = `${100 - (tick / total) * 100}%`;
        return (
          <span key={tick}>
            <div className={styles.sideTick} style={{ top }}>{tick} {unit}</div>
            <div className={styles.tickLine} style={{ top }}/>
          </span>
        );
      })}
    </div>
  );
}

export function LatestBarnFeedLevel({ barnId, heightPx, widthPx }) {
  const barnCurrentFeedLevel = useBarnLatestFeedLevel({ barnId });
  if (barnCurrentFeedLevel.loading) {
    return <LinearProgress/>;
  }

  return <BarnFeedLevelDisplay
    {...barnCurrentFeedLevel}
    widthPx={widthPx}
    heightPx={heightPx}
  />;
}

function getSideTicks(total) {
  return d3.scaleLinear().range(0,100).domain([0, total]).ticks(5).slice(1);
}
